import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, CheckCircle, XCircle, Play, Image as ImageIcon, DollarSign, ShieldCheck, ShieldAlert, AlertTriangle, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import AdsPricingPanel from '../components/admin/AdsPricingPanel';

const AdsValidation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pendingAds, setPendingAds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    loadPendingAds();
  }, [user, navigate]);

  const loadPendingAds = async () => {
    try {
      const response = await axios.get('/api/admin/ads/pending');
      setPendingAds(response.data);
    } catch (error) {
      console.error('Error loading ads:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (adId) => {
    try {
      await axios.post(`/api/admin/ads/${adId}/approve`);
      toast.success('Publicité approuvée');
      loadPendingAds();
    } catch (error) {
      console.error('Error approving ad:', error);
      toast.error('Erreur');
    }
  };

  const handleReject = async (adId) => {
    const reason = window.prompt('Raison du rejet (optionnel):');
    try {
      await axios.post(`/api/admin/ads/${adId}/reject`, { reason });
      toast.success('Publicité rejetée');
      loadPendingAds();
    } catch (error) {
      console.error('Error rejecting ad:', error);
      toast.error('Erreur');
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-black text-white flex items-center justify-center">Chargement...</div>;
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Header */}
        <div className="flex items-center space-x-4 mb-6">
          <button
            onClick={() => navigate('/admin')}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
              Validation des Publicités
            </h1>
            <p className="text-gray-400 mt-1">Approuvez ou rejetez les publicités en attente</p>
          </div>
        </div>

        {/* Ads pricing editor (v50.10.1) — changes reflect instantly on the PRO side */}
        <AdsPricingPanel />

        {/* Pending Ads */}
        {pendingAds.length === 0 ? (
          <div className="text-center py-20 bg-white/5 rounded-3xl border border-white/10">
            <DollarSign className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-2xl font-semibold mb-2">Aucune publicité en attente</h3>
            <p className="text-gray-400">Toutes les publicités ont été validées</p>
          </div>
        ) : (
          <div className="space-y-6">
            {pendingAds.map((ad) => {
              const mod = ad.auto_moderation || {};
              const autoFlagged = !!mod.flagged;
              const isAlreadyLive = ad.status === 'active';
              return (
              <div key={ad.id} className="bg-white/5 border border-white/10 rounded-3xl p-6" data-testid={`ad-review-${ad.id}`}>
                {/* Auto-moderation banner */}
                <div className={`mb-4 flex items-start gap-3 px-4 py-3 rounded-xl border ${
                  autoFlagged
                    ? 'bg-red-500/10 border-red-500/40 text-red-300'
                    : isAlreadyLive
                      ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300'
                      : 'bg-slate-500/10 border-slate-500/40 text-slate-300'
                }`} data-testid={`ad-moderation-banner-${ad.id}`}>
                  {autoFlagged
                    ? <ShieldAlert className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    : isAlreadyLive
                      ? <ShieldCheck className="w-5 h-5 mt-0.5 flex-shrink-0" />
                      : <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    {autoFlagged ? (
                      <>
                        <p className="font-semibold text-sm">Modération auto : SIGNALÉ — non publié</p>
                        <p className="text-xs mt-0.5 opacity-90">
                          {mod.category ? `Catégorie : ${mod.category}` : ''}{mod.reason ? ` · ${mod.reason}` : ''}
                        </p>
                      </>
                    ) : isAlreadyLive ? (
                      <>
                        <p className="font-semibold text-sm flex items-center gap-2">
                          <Eye className="w-4 h-4" /> Déjà publiée — modération auto OK
                        </p>
                        <p className="text-xs mt-0.5 opacity-90">Seconde revue manuelle requise.</p>
                      </>
                    ) : (
                      <p className="font-semibold text-sm">En attente de revue (legacy)</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Ad Preview */}
                  <div>
                    <h3 className="font-semibold mb-4">Aperçu</h3>
                    {ad.ad_type === 'video' ? (
                      <video
                        controls
                        className="w-full rounded-2xl bg-black"
                        src={ad.content_url}
                      >
                        Votre navigateur ne supporte pas la vidéo.
                      </video>
                    ) : ad.ad_type === 'audio' ? (
                      <div className="w-full rounded-2xl bg-gradient-to-br from-fuchsia-900/40 to-violet-900/30 p-6 flex flex-col items-center justify-center">
                        <Play className="w-12 h-12 text-fuchsia-300 mb-3" />
                        <audio controls src={ad.content_url} className="w-full max-w-sm" />
                      </div>
                    ) : (
                      <img
                        src={ad.content_url}
                        alt="Publicité"
                        className="w-full rounded-2xl"
                      />
                    )}
                    {/* Transcript preview for audio/video */}
                    {(mod.transcript || mod.reason === 'transcription_unavailable') && (
                      <details className="mt-3 text-xs text-gray-400">
                        <summary className="cursor-pointer text-gray-300 hover:text-white">Transcription</summary>
                        <p className="mt-2 italic">{mod.transcript || 'Transcription indisponible'}</p>
                      </details>
                    )}
                  </div>

                  {/* Ad Info */}
                  <div>
                    <h3 className="font-semibold mb-4">Informations</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-400">Annonceur</p>
                        <p className="font-medium">{ad.user?.name || ad.user?.email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Type</p>
                        <div className="flex items-center space-x-2">
                          {ad.ad_type === 'video' ? (
                            <Play className="w-4 h-4" />
                          ) : (
                            <ImageIcon className="w-4 h-4" />
                          )}
                          <span className="capitalize">{ad.ad_type}</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Durée</p>
                        <p className="font-medium">{ad.duration_days} jours</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Zones ciblées</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {ad.zones?.filter(z => z !== 'shortfilms').map((zone, i) => (
                            <span key={i} className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded-full text-xs">
                              {zone}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Prix</p>
                        <p className="font-bold text-xl text-green-400">{ad.price}€</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-400">Statut paiement</p>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          ad.payment_status === 'paid'
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-orange-500/20 text-orange-400'
                        }`}>
                          {ad.payment_status === 'paid' ? 'Payé' : 'En attente'}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-3 mt-6">
                      <button
                        onClick={() => handleReject(ad.id)}
                        className="flex-1 px-6 py-3 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-full font-semibold transition-colors flex items-center justify-center space-x-2"
                        data-testid={`ad-reject-${ad.id}`}
                      >
                        <XCircle className="w-5 h-5" />
                        <span>{isAlreadyLive ? 'Retirer' : 'Rejeter'}</span>
                      </button>
                      <button
                        onClick={() => handleApprove(ad.id)}
                        className="flex-1 px-6 py-3 bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-full font-semibold transition-colors flex items-center justify-center space-x-2"
                        data-testid={`ad-approve-${ad.id}`}
                      >
                        <CheckCircle className="w-5 h-5" />
                        <span>{isAlreadyLive ? 'Confirmer' : 'Approuver'}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );})}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdsValidation;
